import PropTypes from 'prop-types';
import { styled, alpha } from '@mui/material/styles';
import { ButtonBase, CircularProgress, Tooltip, Box } from '@mui/material';
import { Icon } from '@iconify/react';

/**
 * Entry point to the Data Access Console.
 *
 * Styled from the Interval wordmark rather than the MUI palette — the purple
 * of "INT" running into the two blues of "ERVAL" — so the button reads as the
 * same product the console it opens belongs to. It is the only filled control
 * in the toolbar, which is deliberate: everything else there is a utility,
 * this one leaves the portal.
 */

const BRAND = {
  purple: '#7B4B8E',
  blueDeep: '#1D6FB8',
  blue: '#3399CF',
};

const GRADIENT = `linear-gradient(135deg, ${BRAND.purple} 0%, ${BRAND.blueDeep} 58%, ${BRAND.blue} 100%)`;

const Root = styled(ButtonBase, {
  shouldForwardProp: (prop) => prop !== 'compact',
})(({ theme, compact }) => ({
  position: 'relative',
  overflow: 'hidden',
  height: 34,
  minWidth: compact ? 34 : 'auto',
  width: compact ? 34 : 'auto',
  padding: compact ? 0 : '0 16px 0 13px',
  borderRadius: 999,
  color: '#fff',
  fontFamily: theme.typography.fontFamily,
  fontSize: 12.5,
  fontWeight: 600,
  letterSpacing: '0.015em',
  whiteSpace: 'nowrap',
  background: GRADIENT,
  // Two shadows: a tight one to seat the button on the toolbar, and a wider
  // tinted one that only appears on hover.
  boxShadow: `0 1px 2px ${alpha('#101828', 0.18)}`,
  transition: theme.transitions.create(['box-shadow', 'transform', 'filter'], {
    duration: 180,
  }),

  '&:hover': {
    filter: 'brightness(1.07)',
    transform: 'translateY(-1px)',
    boxShadow: `0 6px 18px -4px ${alpha(BRAND.blueDeep, 0.55)}, 0 1px 2px ${alpha('#101828', 0.2)}`,
  },
  // Pressed returns it to the surface, so the click has somewhere to go.
  '&:active': {
    transform: 'translateY(0)',
    filter: 'brightness(0.97)',
  },
  '&.Mui-focusVisible': {
    outline: `2px solid ${alpha(BRAND.blue, 0.9)}`,
    outlineOffset: 2,
  },
  '&.Mui-disabled': {
    opacity: 0.65,
    filter: 'saturate(0.7)',
    transform: 'none',
    boxShadow: `0 1px 2px ${alpha('#101828', 0.14)}`,
  },

  // A sheen that crosses once on hover. Sits under the label via z-index
  // rather than over it, so text never dims as it passes.
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    background: `linear-gradient(105deg, transparent 25%, ${alpha('#fff', 0.28)} 50%, transparent 75%)`,
    transform: 'translateX(-120%)',
    transition: 'transform .65s ease',
    pointerEvents: 'none',
  },
  '&:hover::after': { transform: 'translateX(120%)' },

  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
    '&:hover': { transform: 'none' },
    '&::after': { display: 'none' },
  },
}));

const Label = styled('span')({
  position: 'relative',
  zIndex: 1,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 7,
  lineHeight: 1,
});

export default function DataAccessButton({ loading, onClick, compact = false }) {
  const button = (
    <Root
      compact={compact ? 1 : 0}
      onClick={onClick}
      disabled={loading}
      disableRipple={false}
      aria-label="Open the Data Access Console"
      aria-busy={loading || undefined}
    >
      <Label>
        {loading ? (
          <CircularProgress size={15} thickness={5} sx={{ color: '#fff' }} />
        ) : (
          <Icon icon="mdi:database-search-outline" width={17} />
        )}
        {!compact && <span>{loading ? 'Opening…' : 'Data Access'}</span>}
      </Label>
    </Root>
  );

  // The icon-only form has no visible label, so it needs the tooltip to say
  // what it does. The full-width form already says it.
  return compact ? (
    <Tooltip title="Data Access Console" arrow>
      <Box component="span">{button}</Box>
    </Tooltip>
  ) : (
    button
  );
}

DataAccessButton.propTypes = {
  loading: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  /** Icon-only, for narrow toolbars. */
  compact: PropTypes.bool,
};
